import { z } from "zod";
import type { Args, Operation } from "../types";
import { scoped } from "../types";
import { idNumericParams } from "./_common";

export const overallRating = z.enum(["strong_yes", "yes", "no", "strong_no"]);
export type OverallRating = z.infer<typeof overallRating>;

export const questionKind = z.enum(["rating", "text", "yes_no"]);

export const scorecardQuestionSchema = z.object({
  id: z.number(),
  template_id: z.number(),
  slug: z.string(),
  prompt: z.string(),
  position: z.number(),
  kind: questionKind,
  archived_at: z.string().nullable(),
});
export type ScorecardQuestionShape = z.infer<typeof scorecardQuestionSchema>;

export const scorecardTemplateSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  slug: z.string(),
  name: z.string(),
  applies_to_stage_kind: z.string().nullable(),
  is_default: z.number(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  questions: z.array(scorecardQuestionSchema),
});
export type ScorecardTemplateShape = z.infer<typeof scorecardTemplateSchema>;

export const scorecardAnswerSchema = z.object({
  scorecard_id: z.number(),
  question_id: z.number(),
  answer_text: z.string().nullable(),
  answer_rating: z.string().nullable(),
});

export const scorecardSchema = z.object({
  id: z.number(),
  application_id: z.number(),
  stage_id: z.number().nullable(),
  template_id: z.number(),
  template_slug: z.string(),
  template_name: z.string(),
  author_actor_id: z.number().nullable(),
  author_display_name: z.string().nullable(),
  overall_rating: overallRating.nullable(),
  body: z.string().nullable(),
  submitted_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  answers: z.array(scorecardAnswerSchema),
});
export type ScorecardShape = z.infer<typeof scorecardSchema>;

export const templatesList: Operation<void, void, void, ScorecardTemplateShape[]> = {
  method: "GET",
  path: () => "/hiring/api/scorecard-templates",
  response: z.array(scorecardTemplateSchema),
};

export const templateCreateBody = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  applies_to_stage_kind: z.string().nullable().optional(),
  is_default: z.boolean().optional(),
});

// coverage-ignore: mutate ops deferred on Hono (hiring.ts NOTE); list/submit mounted
export const templatesCreate: Operation<
  void,
  void,
  z.infer<typeof templateCreateBody>,
  ScorecardTemplateShape
> = {
  method: "POST",
  path: () => "/hiring/api/scorecard-templates",
  body: templateCreateBody,
  response: scorecardTemplateSchema,
  // phase-2 endpoint still deferred; declaration is ready for when it mounts.
  invalidates: [templatesList],
};

export const questionAddBody = z.object({
  slug: z.string().min(1),
  prompt: z.string().min(1),
  position: z.number().int().optional(),
  kind: questionKind,
});

// coverage-ignore: mutate ops deferred on Hono (hiring.ts NOTE); list/submit mounted
export const questionsAdd: Operation<
  { id: number },
  void,
  z.infer<typeof questionAddBody>,
  ScorecardQuestionShape
> = {
  method: "POST",
  path: ({ id }) => `/hiring/api/scorecard-templates/${id}/questions`,
  params: idNumericParams,
  body: questionAddBody,
  response: scorecardQuestionSchema,
  // questions are embedded on templatesList rows.
  invalidates: [templatesList],
};

// coverage-ignore: mutate ops deferred on Hono (hiring.ts NOTE); list/submit mounted
export const questionsArchive: Operation<
  { id: number; questionId: number },
  void,
  void,
  { id: number }
> = {
  method: "DELETE",
  path: ({ id, questionId }) =>
    `/hiring/api/scorecard-templates/${id}/questions/${questionId}`,
  params: z.object({
    id: z.coerce.number().int(),
    questionId: z.coerce.number().int(),
  }),
  response: z.object({ id: z.number() }),
  invalidates: [templatesList],
};

export const scorecardSubmitBody = z.object({
  template_slug: z.string().optional(),
  template_id: z.number().int().optional(),
  stage_id: z.number().int().nullable().optional(),
  overall_rating: overallRating,
  body: z.string().optional(),
  answers: z
    .array(
      z.object({
        question_slug: z.string(),
        text: z.string().optional(),
        rating: z.string().optional(),
      }),
    )
    .optional(),
});

export const scorecardsForApplication: Operation<
  { id: number },
  void,
  void,
  ScorecardShape[]
> = {
  method: "GET",
  path: ({ id }) => `/hiring/api/applications/${id}/scorecards`,
  params: idNumericParams,
  response: z.array(scorecardSchema),
};

export const scorecardsSubmit: Operation<
  { id: number },
  void,
  z.infer<typeof scorecardSubmitBody>,
  ScorecardShape
> = {
  method: "POST",
  path: ({ id }) => `/hiring/api/applications/${id}/scorecards`,
  params: idNumericParams,
  body: scorecardSubmitBody,
  response: scorecardSchema,
  // appends to the per-application scorecard list (path param is application id).
  invalidates: [
    scoped(
      scorecardsForApplication,
      (args: Args<{ id: number }, void, unknown>) => ({ id: args.params.id }),
    ),
  ],
};

export const hiringScorecards = {
  templatesList,
  templatesCreate,
  questionsAdd,
  questionsArchive,
  scorecardsSubmit,
  scorecardsForApplication,
};
